import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import cn from 'classnames';

import useAnchor, { anchorHref } from 'components/hooks/useAnchor';
import { APP_STORE_URL, GOOGLE_PLAY_URL, SOCIAL_LINKS } from 'src/constants/misc';

import { AppleIcon, FacebookIcon, GooglePlayIcon, InstagramIcon, LinkedInIcon } from '../Icons';
import Logo from '../Logo';

import style from './Footer.module.scss';

export const STORES_PING_EVENT = 'avalio:stores-ping';

const STORES = [
  { name: 'App Store', url: APP_STORE_URL, Icon: AppleIcon, prefix: 'footer.downloadOn', label: 'footer.appStore' },
  { name: 'Google Play', url: GOOGLE_PLAY_URL, Icon: GooglePlayIcon, prefix: 'footer.getItOn', label: 'footer.googlePlay' }
];

const SOCIAL = [
  { id: 'facebook', Icon: FacebookIcon, label: 'footer.facebook' },
  { id: 'instagram', Icon: InstagramIcon, label: 'footer.instagram' },
  { id: 'linkedin', Icon: LinkedInIcon, label: 'footer.linkedin' }
];

const Footer = ({ homePath = '/', social = false }) => {
  const { t } = useTranslation();
  const onAnchorClick = useAnchor(homePath);
  const storesRef = useRef(null);

  // Highlight store badges when the phone mic button is pressed
  useEffect(() => {
    const ping = () => {
      const stores = storesRef.current;
      stores.classList.remove(style.ping);
      void stores.offsetWidth;
      stores.classList.add(style.ping);
      stores.querySelector('a')?.focus({ preventScroll: true });
    };
    window.addEventListener(STORES_PING_EVENT, ping);
    return () => window.removeEventListener(STORES_PING_EVENT, ping);
  }, []);

  return (
    <footer className={cn('tone-dark', style.footer)}>
      <div className={cn('wrap', style.inner)}>
        <div className={style.brand}>
          <span className={style.logo}>
            <Logo label='Avalio' />
          </span>
          <div
            className={style.stores}
            id='pobierz'
            ref={storesRef}
          >
            {STORES.map(({ name, url, Icon, prefix, label }) => {
              const content = (
                <>
                  <Icon />
                  <span>
                    <small>{t(prefix)}</small>
                    <b>{name}</b>
                  </span>
                </>
              );
              return url ? (
                <a
                  key={name}
                  className={style.store}
                  href={url}
                  aria-label={t(label)}
                >
                  {content}
                </a>
              ) : (
                <span
                  key={name}
                  className={cn(style.store, style.unavailable)}
                >
                  {content}
                </span>
              );
            })}
            {STORES.some((store) => !store.url) && (
              <span className={style.soon}>
                <span>
                  <i />
                  {t('footer.comingSoon')}
                </span>
              </span>
            )}
          </div>
        </div>
        <nav aria-label={t('footer.label')}>
          <a
            href={anchorHref('#agent', homePath)}
            onClick={onAnchorClick}
          >
            {t('navigation.agent')}
          </a>
          <a
            href={anchorHref('#jak', homePath)}
            onClick={onAnchorClick}
          >
            {t('navigation.how')}
          </a>
          <a
            href={anchorHref('#faq', homePath)}
            onClick={onAnchorClick}
          >
            {t('navigation.faq')}
          </a>
          <Link to='/privacy-policy'>{t('navigation.privacyPolicy')}</Link>
          <Link to='/statute'>{t('navigation.statute')}</Link>
        </nav>
        {social && (
          <ul
            className={style.social}
            aria-label={t('footer.social')}
          >
            {SOCIAL.map(({ id, Icon, label }) => (
              <li key={id}>
                <a
                  href={SOCIAL_LINKS[id] || undefined}
                  target='_blank'
                  rel='noreferrer'
                  aria-label={t(label)}
                >
                  <Icon />
                </a>
              </li>
            ))}
          </ul>
        )}
        <span>{t('footer.copyright', { year: new Date().getFullYear() })}</span>
      </div>
    </footer>
  );
};

export default Footer;
